import React from "react";
import { Image, Tag, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";
import { formatCurrency, getFullAwsUrl } from "../../../../helpers/utils";
import NoDataMessage from "../../../Components/NoData";
import { svg } from "../../../../Assets/svg";

interface Order2BSItemsDetailsProps {
  type: "products" | "parts" | "components";
  items: any[];
  currency?: string;
  conversionRate?: number;
}

const formatFinish = (finish?: string) => {
  if (!finish) return "";
  return finish
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const Order2BSItemsDetails: React.FC<Order2BSItemsDetailsProps> = ({
  type,
  items = [],
  currency = "GBP",
  conversionRate = 1,
}) => {
  const getSectionMeta = () => {
    switch (type) {
      case "products":
        return {
          title: "Products",
          singularTitle: "Product",
          subtitle:
            "Showroom Stock Product variants included in this 2BS replenishment order.",
          tagColor: "blue",
          tagLabel: "Product",
          emptyText: "No products included in this 2BS order.",
        };
      case "parts":
        return {
          title: "Parts",
          singularTitle: "Part",
          subtitle:
            "Showroom Stock Parts and accessories included in this 2BS replenishment order.",
          tagColor: "orange",
          tagLabel: "Part",
          emptyText: "No parts included in this 2BS order.",
        };
      case "components":
        return {
          title: "Components",
          singularTitle: "Component",
          subtitle:
            "Showroom Stock Components included in this 2BS replenishment order.",
          tagColor: "purple",
          tagLabel: "Component",
          emptyText: "No components included in this 2BS order.",
        };
    }
  };

  const meta = getSectionMeta();
  return (
    <div className="d-flex flex-column gap-2">
      {/* Section Header */}
      <h3 className="fs-4 fw-bold text-dark mb-1">
        {meta.title} ({items.length})
      </h3>

      {/* Items List */}
      {items.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4 p-5 bg-white text-center">
          <NoDataMessage
            icon={svg?.noShelfProduct}
            text={meta.emptyText}
          />
        </div>
      ) : (
        items.map((item: any, idx: number) => {
          const variant = item?.variant_id && typeof item.variant_id === "object"
            ? item.variant_id
            : null;

          const title = (() => {
            if (type === "products") {
              return (
                variant?.variant_name ||
                item?.part_name ||
                item?.variant_key ||
                `Product Item #${idx + 1}`
              );
            }
            if (type === "parts") {
              return (
                item?.part_name ||
                item?.part_id?.name ||
                item?.variant_key ||
                `Part Item #${idx + 1}`
              );
            }
            return (
              item?.component_name ||
              item?.component_id?.name ||
              item?.variant_key ||
              `Component Item #${idx + 1}`
            );
          })();

          const photo =
            item?.photo ||
            variant?.photo ||
            (Array.isArray(variant?.images) && variant.images[0]) ||
            item?.part_id?.photo ||
            item?.component_id?.photo;

          const code =
            variant?.product_code ||
            (type === "parts" && typeof item?.part_id === "string" ? item.part_id : item?.part_id?.code) ||
            (type === "components" && typeof item?.component_id === "string" ? item.component_id : item?.component_id?.code);

          const cost = Number(variant?.cost || variant?.original_price || 0);
          const orderQty = Number(item?.order_qty || 0);

          // Extract variables
          const variables = (() => {
            if (type === "parts" && Array.isArray(item?.part_variables)) {
              return item.part_variables;
            }
            if (type === "components" && Array.isArray(item?.component_variables)) {
              return item.component_variables;
            }
            return [];
          })();

          return (
            <div
              key={item?.lean_inventory_id || item?._id || idx}
              className="card border-0 shadow-sm rounded-4 bg-white p-3 mb-0"
              style={{
                transition: "all 0.2s ease-in-out",
              }}
            >
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                {/* Left: Thumbnail & Details */}
                <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ minWidth: 0 }}>
                  {/* Photo / Thumbnail */}
                  <div
                    className="rounded-3 border overflow-hidden flex-shrink-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: 78,
                      height: 78,
                      minWidth: 78,
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                    }}
                  >
                    {photo ? (
                      <Image
                        src={getFullAwsUrl(photo)}
                        alt={title}
                        fallback={placeHolderImage}
                        style={{
                          width: 78,
                          height: 78,
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        preview={{
                          mask: <EyeOutlined style={{ fontSize: 16 }} />,
                        }}
                      />
                    ) : (
                      <Image
                        src={placeHolderImage}
                        alt={title}
                        preview={false}
                        style={{
                          width: 78,
                          height: 78,
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    {/* Tags row */}
                    {/* <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                      <Tag
                        color={meta.tagColor}
                        className="m-0 fs-8 fw-semibold px-2 py-0"
                        style={{ borderRadius: "4px" }}
                      >
                        {meta.tagLabel}
                      </Tag>
                    </div> */}

                    {/* Title */}
                    <h5
                      className={`fw-bold text-dark fs-6 ${variables.length > 0 ? "mb-1" : "mb-0"}`}
                      style={{
                        wordBreak: "break-word",
                        lineHeight: 1.35,
                      }}
                    >
                      {title}
                    </h5>

                    {/* Dimensions & Specifications (For Product Variants) */}
                    {/* {variant && (
                      <div className="d-flex align-items-center gap-2 flex-wrap text-secondary fs-7 mb-2">
                        {variant.height !== undefined && variant.height !== null && (
                          <span className="badge bg-light text-dark border px-2 py-1">
                            H: <strong>{variant.height}mm</strong>
                          </span>
                        )}
                        {variant.width !== undefined && variant.width !== null && (
                          <span className="badge bg-light text-dark border px-2 py-1">
                            W: <strong>{variant.width}mm</strong>
                          </span>
                        )}
                        {variant.depth !== undefined && variant.depth !== null && (
                          <span className="badge bg-light text-dark border px-2 py-1">
                            D: <strong>{variant.depth}mm</strong>
                          </span>
                        )}
                        {variant.length !== undefined && variant.length !== null && (
                          <span className="badge bg-light text-dark border px-2 py-1">
                            L: <strong>{variant.length}mm</strong>
                          </span>
                        )}
                        {variant.reinforced_height !== undefined &&
                          variant.reinforced_height !== null && (
                            <span className="badge bg-light text-dark border px-2 py-1">
                              Reinforced:{" "}
                              <strong>{variant.reinforced_height}mm</strong>
                            </span>
                          )}
                        {variant.component_type && (
                          <span className="badge bg-light text-secondary border px-2 py-1">
                            Type: {formatFinish(variant.component_type)}
                          </span>
                        )}
                      </div>
                    )} */}

                    {/* Custom Variables (For Parts & Components) */}
                    {/* {variables.length > 0 && (
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        {variables.map((v: any, vIdx: number) => {
                          const keyLabel = v.key ? formatFinish(v.key) : "Variable";
                          const valStr = `${v.value ?? ""}${v.unit ? ` ${v.unit}` : ""}`;
                          return (
                            <span
                              key={vIdx}
                              className="badge bg-light text-dark border px-2 py-1 fs-8"
                            >
                              <span className="text-secondary me-1">{keyLabel}:</span>
                              <strong>{valStr}</strong>
                            </span>
                          );
                        })}
                      </div>
                    )} */}
                  </div>
                </div>

                {/* Right: Order Quantity Box & Financials */}
                <div className="d-flex align-items-center gap-3 flex-shrink-0 ms-md-auto justify-content-end">
                  {/* {cost > 0 && (
                    <div className="text-end d-none d-sm-block">
                      <div className="text-muted fs-8">Unit Cost</div>
                      <div className="fw-semibold text-dark fs-7">
                        {formatCurrency(cost, currency, conversionRate)}
                      </div>
                      <div className="text-muted fs-8 mt-1">Est. Total</div>
                      <div className="fw-bold text-dark fs-6">
                        {formatCurrency(cost * orderQty, currency, conversionRate)}
                      </div>
                    </div>
                  )} */}

                  <div
                    className="text-center px-3 py-2 rounded-3 d-flex flex-column align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 140,
                      minWidth: 140,
                      height: 78,
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <div
                      className="text-secondary fs-8 text-uppercase fw-semibold mb-1"
                      style={{ letterSpacing: "0.5px" }}
                    >
                      Qty
                    </div>
                    <div
                      className="fw-bolder fs-3 lh-1 text-dark"
                    >
                      {orderQty.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default Order2BSItemsDetails;
